"use client";

import { useState, useEffect, useRef } from "react";
import { LightbulbIcon, Plus, Tag, X, BookText } from "lucide-react";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { TooltipWrapper, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";
import { Switch } from "../ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import AddIcon from "@mui/icons-material/Add";
import ClassificationModal from "../Classification/ClassificationModal";
import { useSelector } from "react-redux";
import fetchData from "../../utils/fetchData";
import DecisionMatrixAIGuard from "./DecisionMatrixAIGuard";

// Add prop for CommentsGuidance
import { API_BASE_URL } from "../../config/config";
import { enableCustomerNaicsCMI } from "../../api/naicsService";

export function DecisionMatrixCard (props) {
  const [open, setOpen] = useState(false);
  const [showCommentsGuidance, setShowCommentsGuidance] = useState(false);
  const [commentsGuidance, setCommentsGuidance] = useState("");
  const [selectedNaics, setSelectedNaics] = useState({});
  const [menuItems, setMenuItems] = useState([]); // State for dynamic menu items
  const [selectedLOB, setSelectedLOB] = useState(""); // Initialize with an empty string
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [showAddClassification, setShowAddClassification] = useState(false);
  const [activeCmi, setActiveCmi] = useState("");
  const [newClassification, setNewClassification] = useState({
    naicsCode: "",
    description: "",
    source: ""
  });
  const [responseData, setResponseData] = useState([]);
  const [disabledCmis, setDisabledCmis] = useState(new Set());
  const [loadingCmis, setLoadingCmis] = useState(new Set());
  const [error, setError] = useState(null);
  const [naicsCmiSectionsEnabled, setNaicsCmiSectionsEnabled] = useState(
    new Set()
  ); // Track which NAICS have CMI sections enabled
  const [naicsCmiSectionsLoading, setNaicsCmiSectionsLoading] = useState(
    new Set()
  ); // Track loading state for each NAICS
  const [naicsCmiSectionsError, setNaicsCmiSectionsError] = useState({}); // Track error state for each NAICS
  const [individualCmiLoading, setIndividualCmiLoading] = useState(new Set()); // Track loading state for individual CMIs
  const [individualCmiError, setIndividualCmiError] = useState({}); // Track error state for individual CMIs
  const aiGuardRef = useRef(null);

  const accessToken = localStorage.getItem("access_token") || "";
  const refreshToken = localStorage.getItem("refresh_token") || "";

  const selectedApplicationId = useSelector(
    state => state.intakeData.selectedAppId
  );

  const selectedApplicationDetails = useSelector(
    state => state.intakeData?.selectedApplicationData
  );

  useEffect(() => {
    if (selectedApplicationId) {
      fetchData(
        `${API_BASE_URL}/insureai/ml/intake/applications/v2/customer_naics_cmi/?application_id=${selectedApplicationId}`,
        "GET"
      ).then(data => {
        setResponseData(data);

        const disabledSet = new Set();
        const enabledNaicsSet = new Set();

        data.forEach(naics => {
          let allEnabled = true;

          naics.cmis?.forEach(cmi => {
            if (!cmi.enable) {
              disabledSet.add(cmi.cmi);
              allEnabled = false;
            }
          });

          if (allEnabled) {
            enabledNaicsSet.add(naics.naics);
          }
        });

        setDisabledCmis(disabledSet);
        setNaicsCmiSectionsEnabled(enabledNaicsSet);

        if (data.length > 0) {
          setSelectedNaics(data[0]);
        }
      });
    }
  }, [selectedApplicationId]);

  useEffect(() => {
    const lobs = selectedApplicationDetails?.lob || "";
    const lobArray = lobs
      .split(",")
      .filter(
        lob =>
          lob.includes("Commercial General Liability") ||
          lob.includes("Commercial Property")
      );

    setMenuItems(lobArray); // Dynamically set menu items
    setSelectedLOB(lobArray[0]); // Set the first item as the default selected value
  }, [selectedApplicationDetails]);

  const handleAddClassification = () => {
    setShowAddClassification(false);
    setNewClassification({
      naicsCode: "",
      description: "",
      source: ""
    });
  };
  const handleCmiToggle = async cmiId => {
    setIndividualCmiLoading(prev => new Set(prev).add(cmiId));
    setIndividualCmiError(prev => ({ ...prev, [cmiId]: null }));
    const isCurrentlyDisabled = disabledCmis.has(cmiId);
    try {
      await enableCustomerNaicsCMI({
        applicationId: selectedApplicationId,
        changeType: "cmi",
        value: cmiId,
        enable: isCurrentlyDisabled,
        accessToken,
        refreshToken
      });
      setDisabledCmis(prev => {
        const newSet = new Set(prev);
        if (isCurrentlyDisabled) {
          newSet.delete(cmiId);
          console.log(`Switch turned ON for CMI ${cmiId}`);
        } else {
          newSet.add(cmiId);
          console.log(`Switch turned OFF for CMI ${cmiId}`);
        }
        return newSet;
      });

      // Trigger the polling after successful CMI toggle
      if (aiGuardRef.current?.triggerAnalysis) {
        aiGuardRef.current.triggerAnalysis();
      }
    } catch (err) {
      setIndividualCmiError(prev => ({
        ...prev,
        [cmiId]: "Failed to update. Try again."
      }));
    } finally {
      setIndividualCmiLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(cmiId);
        return newSet;
      });
    }
  };

  const handleNaicsCmiSectionToggle = async naicsCode => {
    setNaicsCmiSectionsLoading(prev => new Set(prev).add(naicsCode));
    setNaicsCmiSectionsError(prev => ({ ...prev, [naicsCode]: null }));

    const isCurrentlyEnabled = naicsCmiSectionsEnabled.has(naicsCode);
    const relatedNaics = responseData.find(item => item.naics === naicsCode);
    const cmis = relatedNaics?.cmis || [];

    try {
      await enableCustomerNaicsCMI({
        applicationId: selectedApplicationId,
        changeType: "naics",
        value: naicsCode,
        enable: !isCurrentlyEnabled,
        accessToken,
        refreshToken
      });

      setNaicsCmiSectionsEnabled(prev => {
        const newSet = new Set(prev);
        isCurrentlyEnabled ? newSet.delete(naicsCode) : newSet.add(naicsCode);
        return newSet;
      });

      setDisabledCmis(prev => {
        const updated = new Set(prev);
        cmis.forEach(cmi => {
          isCurrentlyEnabled ? updated.add(cmi.cmi) : updated.delete(cmi.cmi);
        });
        return updated;
      });

      // Trigger the polling after successful NAICS section toggle
      if (aiGuardRef.current?.triggerAnalysis) {
        aiGuardRef.current.triggerAnalysis();
      }
    } catch (err) {
      setNaicsCmiSectionsError(prev => ({
        ...prev,
        [naicsCode]: "Failed to toggle section"
      }));
    } finally {
      setNaicsCmiSectionsLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(naicsCode);
        return newSet;
      });
    }
  };

  const handleCommentsGuidanceChange = guidance => {
    setCommentsGuidance(guidance);
  };

  console.log(responseData);
  return (
    <div data-section={`${props["data-section"]}`}>
      <Card className="p-5 bg-white shadow-md">
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <h3 className="font-semibold flex items-center">
              <Tag className="h-4 w-4 mr-1.5 text-[#FF5533]" />
              NAICS Classifications
            </h3>

            <div className="flex items-center gap-4 ml-auto">
              <div
                className={`flex items-center gap-2 border p-2 rounded-md shadow-md text-black-600 hover:text-black-800 cursor-pointer bg-[#FFFFFF] border-[grey-200]`}
                onClick={() => setShowCommentsGuidance(true)}
              >
                <BookText className="h-4 w-4 text-[#FF5533]" />
                <span>Comments & Guidance</span>
              </div>

              <div
                className={`flex items-center gap-2 border p-2 rounded-md shadow-md text-orange-600 hover:text-orange-800 cursor-pointer bg-[#FFF0ED] border-[#FFDED6]`}
                onClick={() => setOpen(true)}
              >
                <AddIcon style={{ fontSize: "0.9rem" }} />
                <span>Add Classification</span>
              </div>
            </div>
          </div>
          <ClassificationModal open={open} onClose={() => setOpen(false)} />

          <Separator className="my-4" />

          <div className="flex gap-2 overflow-x-auto pb-2">
            {responseData.map(naics => (
              <div
                key={naics.naics}
                className={`p-3 rounded-md cursor-pointer transition-all duration-200 w-[180px] ${
                  selectedNaics.naics === naics.naics
                    ? naics.source === "llm"
                      ? "border-b-2 border-orange-500"
                      : "border-b-2 border-blue-500"
                    : ""
                } ${naics.source === "llm" ? "bg-[#FFF8F6]" : "bg-blue-50"}`}
                onClick={() => setSelectedNaics(naics)}
              >
                <div className="flex flex-col items-start">
                  <div className="flex items-center justify-between w-full">
                    <span className=" font-medium">{naics.naics}</span>
                    {naics.source !== "llm" && (
                      <div className="flex items-center gap-1">
                        <Switch
                          enabled={naicsCmiSectionsEnabled.has(naics.naics)}
                          onChange={() => {
                            console.log(
                              "Triggering NAICS toggle for:",
                              naics.naics
                            );
                            handleNaicsCmiSectionToggle(naics.naics);
                          }}
                          className={`cursor-pointer transition-all duration-200 ${
                            naicsCmiSectionsEnabled.has(naics.naics)
                              ? naics.source === "llm"
                                ? "!bg-orange-500"
                                : "!bg-blue-500"
                              : ""
                          }`}
                          disabled={naicsCmiSectionsLoading.has(naics.naics)}
                        />
                        {naicsCmiSectionsLoading.has(naics.naics) && (
                          <span className="ml-1 text-xs text-gray-400">
                            ...
                          </span>
                        )}
                        {naicsCmiSectionsError[naics.naics] && (
                          <span className="ml-1 text-xs text-red-500">
                            {naicsCmiSectionsError[naics.naics]}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <TooltipWrapper>
                    <TooltipTrigger asChild>
                      <p className="text-sm text-gray-600 mt-1 truncate w-full">
                        {naics.naics_description || "---"}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[300px] p-2">
                      <p className="text-sm">
                        {naics.naics_description || "---"}
                      </p>
                    </TooltipContent>
                  </TooltipWrapper>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-12 gap-4 mt-4">
            <div className="col-span-5">
              <Card
                className={`p-4 border shadow-md h-[350px] ${
                  selectedNaics.source === "llm" ? "bg-[#FFF8F6]" : "bg-blue-50"
                }`}
              >
                <div className="flex justify-between items-start mb-3">
                  <h4 className="text-sm font-medium text-gray-700">
                    Classification Details
                  </h4>
                  <Badge
                    variant="outline"
                    className={`${
                      selectedNaics.source === "llm"
                        ? "bg-[#FFF8F6] text-[#FF5533] border-[#FFDED6]"
                        : "bg-blue-50 text-blue-700 border-blue-200"
                    }`}
                  >
                    NAICS {selectedNaics.naics}
                  </Badge>
                </div>

                <div className="space-y-3 overflow-y-auto max-h-[280px] pr-1">
                  <div className="bg-white p-3 rounded-md border border-gray-100">
                    <p className="text-sm text-gray-500 mb-1">Description</p>
                    <p className="text-sm font-medium">
                      {selectedNaics.naics_description || "---"}
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-md border border-gray-100">
                    <p className="text-sm text-gray-500 mb-1">Source</p>
                    <p className="text-sm font-medium">
                      <Badge
                        variant="outline"
                        className={`${
                          selectedNaics.source === "llm"
                            ? "bg-[#FFF8F6] text-[#FF5533] border-[#FFDED6]"
                            : "bg-blue-50 text-blue-700 border-blue-200"
                        }`}
                      >
                        {selectedNaics.source === "llm"
                          ? "NativeOrange"
                          : "Application"}
                      </Badge>
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-md border border-gray-100">
                    <p className="text-sm text-gray-500 mb-1">
                      Confidence Level
                    </p>
                    <p className="text-sm font-medium">--%</p>
                  </div>
                  <div className="bg-white p-3 rounded-md border border-gray-100">
                    <p className="text-sm text-gray-500 mb-1">
                      Related Documents
                    </p>
                    <p className="text-sm font-medium">---</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Right side: CMI cards (was on left) */}
            <div className="col-span-7">
              {naicsCmiSectionsEnabled.has(selectedNaics.naics) ? (
                <Card
                  className={`p-4 border shadow-md h-[350px] ${
                    selectedNaics.source === "llm"
                      ? "bg-[#FFF8F6]"
                      : "bg-blue-50"
                  }`}
                >
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium text-gray-700">
                      CMI Details for NAICS {selectedNaics.naics}
                    </h4>
                  </div>
                  <div className="grid grid-cols-2 gap-3 overflow-y-auto max-h-[280px] pr-1">
                    {selectedNaics.cmis?.map((cmi, index) => (
                      <Card
                        key={index}
                        className={`p-3 border flex flex-col ${
                          disabledCmis.has(cmi.cmi)
                            ? "bg-gray-100 opacity-75"
                            : cmi.enable
                            ? "border-[green-600] bg-green-50"
                            : "border-gray-100 bg-white"
                        }`}
                      >
                        <div>
                          <h4 className="text-sm font-medium mb-1 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span>CMI {cmi.cmi}</span>{" "}
                              {selectedNaics.source !== "llm" && (
                                <Switch
                                  enabled={!disabledCmis.has(cmi.cmi)}
                                  onChange={() => handleCmiToggle(cmi.cmi)}
                                  className="cursor-pointer transition-all duration-200"
                                  disabled={individualCmiLoading.has(cmi.cmi)}
                                />
                              )}
                              {individualCmiLoading.has(cmi.cmi) && (
                                <span className="text-xs text-blue-600 ml-1">
                                  Updating...
                                </span>
                              )}
                            </div>
                            <span>ISOGL: {cmi.iso || "--"}</span>
                            {disabledCmis.has(cmi.cmi) ? (
                              <Badge
                                className={`bg-grey-100 text-grey-700 border-grey-200 text-[10px]`}
                              >
                                {"Not Considered"}
                              </Badge>
                            ) : (
                              <Badge
                                className={`${
                                  cmi.appetite_decision === "Out of Appetite"
                                    ? "bg-red-100 text-red-700 border-red-200"
                                    : cmi.appetite_decision === "Conditional"
                                    ? "bg-yellow-100 text-yellow-700 border-yellow-200"
                                    : "bg-green-100 text-green-700 border-green-200"
                                } text-[10px]`}
                              >
                                {cmi.appetite_decision || "In Appetite"}
                              </Badge>
                            )}
                          </h4>
                          <p
                            className={`text-sm mb-2 mt-4 ${
                              disabledCmis.has(cmi.cmi) ? "text-gray-500" : ""
                            }`}
                          >
                            {cmi.cmi_description
                              ? cmi.cmi_description
                                  .split(". ")
                                  .map(
                                    sentence =>
                                      sentence.charAt(0).toUpperCase() +
                                      sentence.slice(1).toLowerCase()
                                  )
                                  .join(". ")
                              : "---"}
                          </p>
                          {individualCmiLoading.has(cmi.cmi) && (
                            <div className="text-xs text-blue-600">
                              Updating...
                            </div>
                          )}
                          {individualCmiError[cmi.cmi] && (
                            <div className="text-xs text-red-600">
                              {individualCmiError[cmi.cmi]}
                            </div>
                          )}
                        </div>
                        <div className="mt-auto">
                          <div className="grid grid-cols-3 gap-1 border-t pt-2">
                            <div>
                              <p className="text-[10px] text-gray-500">
                                Property
                              </p>
                              <p className="text-[10px] font-medium">
                                {cmi?.lobs?.property || "--"}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-500">
                                GL Prem
                              </p>
                              <p className="text-[10px] font-medium">
                                {cmi?.lobs?.gl_prem_op || "--"}
                              </p>
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-500">
                                GL Prod
                              </p>
                              <p className="text-[10px] font-medium">
                                {cmi?.lobs?.gl_prod_op || "--"}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-gray-500">
                            {disabledCmis.has(cmi.cmi)
                              ? "CMI is disabled"
                              : "CMI is enabled"}
                          </span>
                        </div>
                      </Card>
                    )) || <></>}
                  </div>
                </Card>
              ) : (
                <Card className="p-4 border shadow-md h-[350px] bg-gray-50 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-gray-500 mb-2">CMI Section Disabled</p>
                    <p className="text-sm text-gray-400">
                      Enable CMI toggle for NAICS {selectedNaics.naics} to view
                      details
                    </p>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </div>

        <Separator className="mb-6" />
        {/* <DecisionMatrixCardV2 /> */}
        <DecisionMatrixAIGuard
          ref={aiGuardRef}
          onCommentsGuidanceChange={handleCommentsGuidanceChange}
        />
      </Card>

      {/* Comments & Guidance Dialog */}
      <Dialog
        open={showCommentsGuidance}
        onOpenChange={setShowCommentsGuidance}
      >
        <DialogContent className="sm:max-w-3xl bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <AddIcon
                style={{ fontSize: "1.1rem", color: "#FF5533", marginRight: 8 }}
              />
              Comments & Guidance
            </DialogTitle>
          </DialogHeader>
          <div
            className="prose text-xs max-h-[400px] overflow-y-auto"
            style={{ marginTop: 8 }}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: commentsGuidance || "No guidance available."
              }}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCommentsGuidance(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Existing Recommendations Dialog */}
      <Dialog open={showRecommendations} onOpenChange={setShowRecommendations}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <LightbulbIcon className="h-5 w-5 mr-2 text-yellow-500" />
              Recommendations for CMI {activeCmi}
            </DialogTitle>
            <DialogDescription>
              Review and implement these recommendations to improve risk
              profile.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            {activeCmi &&
              selectedNaics.cmis
                ?.find(cmi => cmi.cmi === activeCmi)
                ?.recommendations?.map((rec, index) => (
                  <div key={index} className="border rounded-md p-3 bg-white">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className=" font-medium">{rec.title}</h3>
                      <Badge
                        variant="outline"
                        className={`${
                          rec.impact === "High"
                            ? "bg-red-50 text-red-700 border-red-200"
                            : ""
                        } ${
                          rec.impact === "Medium"
                            ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                            : ""
                        } ${
                          rec.impact === "Low"
                            ? "bg-green-50 text-green-700 border-green-200"
                            : ""
                        }`}
                      >
                        {rec.impact} Impact
                      </Badge>
                    </div>
                    <p className=" text-gray-600 mb-2">{rec.description}</p>
                    <div className="flex justify-between items-center">
                      <Badge
                        variant="outline"
                        className="bg-gray-50 text-gray-700 border-gray-200"
                      >
                        {rec.type}
                      </Badge>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-sm h-7"
                        >
                          Dismiss
                        </Button>
                        <Button
                          size="sm"
                          className="text-sm h-7 bg-[#FF5533] hover:bg-[#E04A2F] text-white"
                        >
                          Implement
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
          </div>

          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              <X className="h-4 w-4 mr-1" />
            </Button>
          </DialogClose>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showAddClassification}
        onOpenChange={setShowAddClassification}
      >
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Plus className="h-5 w-5 mr-2 text-[#FF5533]" />
              Add New Classification
            </DialogTitle>
            <DialogDescription>
              Enter the details for the new classification.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="naicsCode" className="text-right">
                NAICS Code
              </Label>
              <Input
                id="naicsCode"
                placeholder="e.g. 115112"
                className="col-span-3"
                value={newClassification.naicsCode}
                onChange={e =>
                  setNewClassification({
                    ...newClassification,
                    naicsCode: e.target.value
                  })
                }
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                Description
              </Label>
              <Input
                id="description"
                placeholder="e.g. Soil Preparation, Planting, and Cultivating"
                className="col-span-3"
                value={newClassification.description}
                onChange={e =>
                  setNewClassification({
                    ...newClassification,
                    description: e.target.value
                  })
                }
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="source" className="text-right">
                Source ==
              </Label>
              <div className="col-span-3 flex gap-2">
                <Button
                  type="button"
                  variant={
                    newClassification.source === "llm" ? "outline" : "default"
                  }
                  size="sm"
                  className={
                    newClassification.source !== "llm"
                      ? "bg-[#FF5533] hover:bg-[#E04A2F]  text-white"
                      : ""
                  }
                  onClick={() =>
                    setNewClassification({
                      ...newClassification,
                      source: "Manual"
                    })
                  }
                >
                  Manual
                </Button>
                <Button
                  type="button"
                  variant={
                    newClassification.source === "Application"
                      ? "default"
                      : "outline"
                  }
                  size="sm"
                  className={
                    newClassification.source === "Application"
                      ? "bg-[#FF5533] hover:bg-[#E04A2F]  text-white"
                      : ""
                  }
                  onClick={() =>
                    setNewClassification({
                      ...newClassification,
                      source: "Application"
                    })
                  }
                >
                  Application
                </Button>
                <Button
                  type="button"
                  variant={
                    newClassification.source === "llm" ? "default" : "outline"
                  }
                  size="sm"
                  className={
                    newClassification.source === "llm"
                      ? "bg-[#FF5533] hover:bg-[#E04A2F]  text-white"
                      : ""
                  }
                  onClick={() =>
                    setNewClassification({
                      ...newClassification,
                      source: "llm"
                    })
                  }
                >
                  NativeOrange
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddClassification(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-[#FF5533] hover:bg-[#E04A2F] text-white"
              onClick={handleAddClassification}
              disabled={
                !newClassification.naicsCode || !newClassification.description
              }
            >
              Add Classification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
